import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertBox,
  Button,
  Input,
  InputGroup,
  ModalInfo,
  ModalInfoProps,
} from '@/components';
import { UpdateReflinkRequest } from '@/api';

import { profileSelectors } from '@/redux/selectors';
import { profileActions } from '@/redux/actions';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { Validation } from '@/models';
import { useInputText } from '@/hooks';

import styles from './YourReferralLinksEditModal.module.sass';

interface YourReferralLinksEditModalProps extends ModalInfoProps {
  reflink: any;
}

const YourReferralLinksEditModal: FC<YourReferralLinksEditModalProps> = (
  props,
) => {
  const { reflink, isOpen, onClose } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { loading, error } = useAppSelector(profileSelectors.getProfileSlice);
  const [finished, setFinished] = useState(false);
  const TITLE_VALIDATIONS: Validation[] = useMemo(
    () => [
      {
        name: 'required',
        value: true,
        message: t('translation.field_is_required'),
      },
    ],
    [t],
  );
  const titleField = useInputText(reflink?.title, TITLE_VALIDATIONS);

  useEffect(() => {
    if (finished === loading) return;

    if (finished && finished !== loading) {
      onClose();
    }

    setFinished(loading);
  }, [loading]);

  const handleSubmit = () => {
    if (!loading) {
      if (titleField.isValid) {
        const payload: UpdateReflinkRequest = {
          code: reflink.code,
          title: titleField.value,
        };
        dispatch(profileActions.updateReflink(payload));
      } else {
        titleField.onBlur();
      }
    }
  };

  return (
    <ModalInfo
      isOpen={isOpen}
      title={t('translation.edit_referral')}
      actions={
        <>
          <Button
            type="primary"
            corner="rect"
            minWidth={175}
            disabled={loading}
            isLoading={loading}
            onClick={handleSubmit}
          >
            {t('translation.save_changes_btn')}
          </Button>
          <Button
            type="secondary"
            corner="rect"
            minWidth={175}
            disabled={loading}
            onClick={onClose}
          >
            {t('translation.cancel_btn')}
          </Button>
        </>
      }
      className={styles.modal}
      onClose={onClose}
    >
      <div className={styles.list}>
        <InputGroup
          label={t('translation.referral_name_lbl')}
          className={styles.inputGroup}
        >
          <Input
            id="name"
            name="name"
            type="text"
            value={titleField.value}
            onChange={titleField.onChange}
            onBlur={titleField.onBlur}
            isError={titleField.isDirty && !titleField.isValid}
            disabled={loading}
          />
          {titleField.isDirty && !titleField.isValid && (
            <AlertBox type="danger" small>
              {{
                description: titleField.errors[0],
              }}
            </AlertBox>
          )}
        </InputGroup>
      </div>
    </ModalInfo>
  );
};

export default YourReferralLinksEditModal;
